/**
 * Browser playback engines. The server decides WHAT plays; an engine only knows
 * how to make it audible through the Mac's default output (the Focusrite).
 */

export interface PlayableTrack {
  id: string;
  title: string;
  artist: string;
  durationMs: number;
  previewUrl?: string | null;
}

export interface PlayOptions {
  offsetMs?: number;
  /** Crossfade / fade-in duration. */
  fadeMs?: number;
}

type Listener<T> = (payload: T) => void;

export interface PlayerEngineEvents {
  ended: { trackId: string };
  error: { trackId: string | null; message: string };
}

export interface PlayerEngine {
  readonly kind: 'preview' | 'musickit';
  /** Plays a track, fading out whatever was playing. */
  play(track: PlayableTrack, opts?: PlayOptions): Promise<void>;
  pause(): void;
  resume(): void;
  stop(fadeMs?: number): Promise<void>;
  /** Effective output volume 0..1 (master × ducking), applied smoothly. */
  setVolume(v: number, rampMs?: number): void;
  currentTrackId(): string | null;
  positionMs(): number;
  durationMs(): number;
  isPlaying(): boolean;
  on<K extends keyof PlayerEngineEvents>(event: K, cb: Listener<PlayerEngineEvents[K]>): () => void;
  destroy(): void;
}

class Emitter {
  private listeners = new Map<string, Set<Listener<unknown>>>();
  on<K extends keyof PlayerEngineEvents>(event: K, cb: Listener<PlayerEngineEvents[K]>): () => void {
    const set = this.listeners.get(event) ?? new Set();
    set.add(cb as Listener<unknown>);
    this.listeners.set(event, set);
    return () => set.delete(cb as Listener<unknown>);
  }
  emit<K extends keyof PlayerEngineEvents>(event: K, payload: PlayerEngineEvents[K]): void {
    for (const cb of this.listeners.get(event) ?? []) cb(payload);
  }
}

const rampGenerations = new WeakMap<object, number>();

/**
 * Animates a numeric property from its current value to `to`. A newer ramp on
 * the same `owner` cancels the older one (two quick skips must not let a stale
 * fade-out silence the deck that is now playing). Resolves `true` if it ran to
 * the end, `false` if it was superseded.
 */
function ramp(owner: object, get: () => number, set: (v: number) => void, to: number, ms: number): Promise<boolean> {
  const gen = (rampGenerations.get(owner) ?? 0) + 1;
  rampGenerations.set(owner, gen);
  const from = get();
  if (ms <= 0 || Math.abs(from - to) < 0.001) {
    set(to);
    return Promise.resolve(true);
  }
  const start = performance.now();
  return new Promise((resolve) => {
    const step = () => {
      if (rampGenerations.get(owner) !== gen) return resolve(false);
      const k = Math.min(1, (performance.now() - start) / ms);
      set(from + (to - from) * k);
      if (k < 1) setTimeout(step, 40);
      else resolve(true);
    };
    step();
  });
}

// ---------------------------------------------------------------------------
// Preview engine: 30 s extracts through two <audio> decks with a real crossfade.
// Works without any Apple account — for development and rehearsals.

interface Deck {
  audio: HTMLAudioElement;
  trackId: string | null;
  gain: number;
}

export class PreviewEngine extends Emitter implements PlayerEngine {
  readonly kind = 'preview' as const;
  private decks: [Deck, Deck];
  private active = 0;
  private volume = 0.8;
  private readonly volumeOwner = {};

  constructor() {
    super();
    const mk = (): Deck => {
      const audio = new Audio();
      audio.preload = 'auto';
      return { audio, trackId: null, gain: 0 };
    };
    this.decks = [mk(), mk()];
    this.decks.forEach((deck, i) => {
      deck.audio.addEventListener('ended', () => {
        if (i === this.active && deck.trackId) this.emit('ended', { trackId: deck.trackId });
      });
      deck.audio.addEventListener('error', () => {
        if (i === this.active) this.emit('error', { trackId: deck.trackId, message: 'audio error' });
      });
    });
  }

  private apply(deck: Deck) {
    deck.audio.volume = Math.max(0, Math.min(1, deck.gain * this.volume));
  }

  async play(track: PlayableTrack, opts: PlayOptions = {}): Promise<void> {
    if (!track.previewUrl) {
      this.emit('error', { trackId: track.id, message: 'no preview' });
      return;
    }
    const fadeMs = opts.fadeMs ?? 2500;
    const previous = this.decks[this.active]!;
    this.active = 1 - this.active;
    const next = this.decks[this.active]!;
    next.trackId = track.id;
    next.audio.src = track.previewUrl;
    next.audio.currentTime = (opts.offsetMs ?? 0) / 1000;
    next.gain = 0;
    this.apply(next);
    try {
      await next.audio.play();
    } catch (e) {
      this.emit('error', { trackId: track.id, message: String(e) });
      return;
    }
    const fadeOut = ramp(
      previous,
      () => previous.gain,
      (v) => {
        previous.gain = v;
        this.apply(previous);
      },
      0,
      fadeMs,
    ).then((done) => {
      if (!done) return;
      previous.audio.pause();
      previous.trackId = null;
    });
    const fadeIn = ramp(
      next,
      () => next.gain,
      (v) => {
        next.gain = v;
        this.apply(next);
      },
      1,
      fadeMs,
    );
    await Promise.all([fadeOut, fadeIn]);
  }

  pause() {
    this.decks[this.active]!.audio.pause();
  }

  resume() {
    const deck = this.decks[this.active]!;
    if (deck.trackId) void deck.audio.play().catch(() => undefined);
  }

  async stop(fadeMs = 1500) {
    const deck = this.decks[this.active]!;
    if (!(await ramp(deck, () => deck.gain, (v) => { deck.gain = v; this.apply(deck); }, 0, fadeMs))) return;
    deck.audio.pause();
    deck.trackId = null;
  }

  setVolume(v: number, rampMs = 600) {
    void ramp(this.volumeOwner, () => this.volume, (x) => {
      this.volume = x;
      this.decks.forEach((d) => this.apply(d));
    }, v, rampMs);
  }

  currentTrackId() {
    return this.decks[this.active]!.trackId;
  }
  positionMs() {
    return this.decks[this.active]!.audio.currentTime * 1000;
  }
  durationMs() {
    const d = this.decks[this.active]!.audio.duration;
    return Number.isFinite(d) ? d * 1000 : 0;
  }
  isPlaying() {
    const a = this.decks[this.active]!.audio;
    return !a.paused && !a.ended;
  }
  destroy() {
    this.decks.forEach((d) => {
      d.audio.pause();
      d.audio.src = '';
    });
  }
}

// ---------------------------------------------------------------------------
// MusicKit JS v3: full tracks with the organiser's Apple Music subscription.
// Requires a developer token (Apple Developer Program) served by our backend.

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    MusicKit?: any;
  }
}

const MUSICKIT_SRC = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';

export async function loadMusicKit(): Promise<any> {
  if (window.MusicKit) return window.MusicKit;
  await new Promise<void>((resolve, reject) => {
    document.addEventListener('musickitloaded', () => resolve(), { once: true });
    const s = document.createElement('script');
    s.src = MUSICKIT_SRC;
    s.async = true;
    s.dataset.webComponents = '';
    s.onerror = () => reject(new Error('MusicKit JS could not be loaded'));
    document.head.appendChild(s);
  });
  return window.MusicKit;
}

export class MusicKitEngine extends Emitter implements PlayerEngine {
  readonly kind = 'musickit' as const;
  private music: any = null;
  private trackId: string | null = null;
  private volume = 0.8;
  private gain = 1;
  private readonly gainOwner = {};
  private readonly volumeOwner = {};

  /** Configures MusicKit and asks the organiser to sign in to Apple Music. */
  async init(developerToken: string): Promise<void> {
    const MusicKit = await loadMusicKit();
    await MusicKit.configure({ developerToken, app: { name: 'KaroZik', build: '1.0.0' } });
    this.music = MusicKit.getInstance();
    this.music.addEventListener('playbackStateDidChange', ({ state }: { state: number }) => {
      const S = MusicKit.PlaybackStates;
      if ((state === S.completed || state === S.ended) && this.trackId) {
        this.emit('ended', { trackId: this.trackId });
      }
    });
    this.music.addEventListener('mediaPlaybackError', (e: unknown) => {
      this.emit('error', { trackId: this.trackId, message: String((e as any)?.message ?? e) });
    });
    this.apply();
  }

  isAuthorized(): boolean {
    return !!this.music?.isAuthorized;
  }

  async authorize(): Promise<void> {
    await this.music.authorize();
  }

  private apply() {
    if (this.music) this.music.volume = Math.max(0, Math.min(1, this.gain * this.volume));
  }

  async play(track: PlayableTrack, opts: PlayOptions = {}): Promise<void> {
    const fadeMs = opts.fadeMs ?? 2500;
    if (this.trackId && this.isPlaying()) {
      await ramp(this.gainOwner, () => this.gain, (v) => { this.gain = v; this.apply(); }, 0, fadeMs / 2);
    }
    this.trackId = track.id;
    this.gain = 0;
    this.apply();
    try {
      await this.music.setQueue({ song: track.id, startPlaying: true });
      if (opts.offsetMs) await this.music.seekToTime(opts.offsetMs / 1000);
    } catch (e) {
      this.emit('error', { trackId: track.id, message: String(e) });
      return;
    }
    await ramp(this.gainOwner, () => this.gain, (v) => { this.gain = v; this.apply(); }, 1, fadeMs / 2);
  }

  pause() {
    this.music?.pause();
  }
  resume() {
    if (this.trackId) void this.music?.play();
  }
  async stop(fadeMs = 1500) {
    await ramp(this.gainOwner, () => this.gain, (v) => { this.gain = v; this.apply(); }, 0, fadeMs);
    await this.music?.stop();
    this.trackId = null;
  }
  setVolume(v: number, rampMs = 600) {
    void ramp(this.volumeOwner, () => this.volume, (x) => { this.volume = x; this.apply(); }, v, rampMs);
  }
  currentTrackId() {
    return this.trackId;
  }
  positionMs() {
    return (this.music?.currentPlaybackTime ?? 0) * 1000;
  }
  durationMs() {
    return (this.music?.currentPlaybackDuration ?? 0) * 1000;
  }
  isPlaying() {
    return !!this.music?.isPlaying;
  }
  destroy() {
    void this.music?.stop();
  }
}
